/**
 * New sessions: which sessions the owner has not looked at yet, so the shell
 * can say "3 new" (the sidebar's recent tests, Home's test lines) and the
 * Sessions tab can mark the rows, as the product demo shows.
 *
 * Kept in this browser only (localStorage, per signed-in person and test). No
 * Firestore field: a seen time that follows the person across devices is a
 * small product step for later (a field on user/{uid}), and it would need a
 * rules check.
 *
 * The first time this runs for a person it records "now" as the starting
 * point, so sessions from before this shipped are never all marked new.
 * Opening a test's Sessions tab marks that test as seen.
 *
 * Every read and write is best effort: with storage blocked nothing is new.
 */
import { isOwnerPreview } from '../pages/UserTesting/insights/insightsModel';

export const SEEN_EVENT = 'aero-sessions-seen';
const key = (uid) => `aero_sessions_seen:${uid}`;

function read(uid) {
  try {
    const raw = window.localStorage.getItem(key(uid));
    const v = raw ? JSON.parse(raw) : null;
    return v && typeof v === 'object' ? v : null;
  } catch (e) {
    return null;
  }
}

function write(uid, v) {
  try {
    window.localStorage.setItem(key(uid), JSON.stringify(v));
    return true;
  } catch (e) {
    return false;
  }
}

/** When this person last looked at this test's sessions (ms), or null when unknown. */
export function getSeenAt(uid, testId) {
  if (!uid) return null;
  let v = read(uid);
  if (!v || typeof v.base !== 'number') {
    v = { base: Date.now(), tests: (v && v.tests) || {} };
    if (!write(uid, v)) return null;
  }
  const t = v.tests && v.tests[testId];
  return typeof t === 'number' ? t : v.base;
}

/** Mark a test's sessions as seen now, and tell the shell. */
export function markSeen(uid, testId) {
  if (!uid || !testId) return;
  const v = read(uid) || { base: Date.now(), tests: {} };
  v.tests = { ...(v.tests || {}), [testId]: Date.now() };
  write(uid, v);
  try {
    window.dispatchEvent(new CustomEvent(SEEN_EVENT, { detail: { testId } }));
  } catch (e) { /* no window */ }
}

const ms = (v) => {
  if (!v) return 0;
  if (v instanceof Date) return v.getTime();
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  if (typeof v.toMillis === 'function') return v.toMillis();
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
};

/** A session counts once it has ended (not still running or abandoned). */
export function sessionEndedAt(s) {
  if (!s || s.status === 'in_progress' || s.status === 'abandoned') return 0;
  return ms(s.completedAt) || (s.status === 'incomplete' ? ms(s.startedAt) : 0);
}

/** New = ended after the seen time, and not the owner's own preview run. */
export function isNewSession(s, seenAt) {
  if (seenAt == null || isOwnerPreview(s)) return false;
  return sessionEndedAt(s) > seenAt;
}

export function countNew(sessions, seenAt) {
  return (sessions || []).filter((s) => isNewSession(s, seenAt)).length;
}
